import React, { useEffect, useState } from "react";
import { getVendorDashboard, updateOrderStatus } from "../../services/api";
import VendorLayout from "../../components/VendorLayout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "../../vendor-panel.css";

const VendorAllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [profile, setProfile] = useState(null);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await getVendorDashboard();
      setOrders(res.data.orders);
      setProfile(res.data.profile);
    } catch (err) {
      alert("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      alert("Failed to update order status.");
    }
  };

  const COMPANY_DETAILS = {
    name: "V ANGEL COIN PVT LTD",
    address: "PGD, PHILA BUNGLOW, DHARAVI MAIN ROAD, DHARAVI",
    city: "MUMBAI - 400017, MAHARASHTRA",
    state: "Maharashtra, Code - 27",
    email: "info@vangelcoin.com",
    gst: "27AAICV4001J7MAHARASHTRA",
    phone: "+91 95940 16963",
  };

  const generateInvoice = (order, vendor) => {
    if (!order || !vendor) return;
    const doc = new jsPDF("p", "mm", "a4");
    const PW = 210;
    const MARGIN = 15;
    const INNER = PW - MARGIN * 2;
    const MID = MARGIN + INNER / 2;
    let y = MARGIN;

    // Helper functions
    const line = (x1, y1, x2, y2) => doc.line(x1, y1, x2, y2);
    const rect = (x, y, w, h, style) => doc.rect(x, y, w, h, style);

    // 1. OUTER BORDER
    rect(MARGIN, MARGIN, INNER, 267);

    // 2. HEADER - Platform/Marketplace Details
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(COMPANY_DETAILS.name, PW / 2, y + 8, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(COMPANY_DETAILS.address, PW / 2, y + 13, { align: "center" });
    doc.text(COMPANY_DETAILS.city, PW / 2, y + 17.5, { align: "center" });
    doc.text(`State: ${COMPANY_DETAILS.state}   |   GSTIN: ${COMPANY_DETAILS.gst}`, PW / 2, y + 22, { align: "center" });
    doc.text(`Email: ${COMPANY_DETAILS.email}   |   Ph: ${COMPANY_DETAILS.phone}`, PW / 2, y + 26.5, { align: "center" });

    // Header divider
    line(MARGIN, y + 30, MARGIN + INNER, y + 30);

    // Title Strip
    doc.setFillColor(240, 246, 252);
    rect(MARGIN, y + 30, INNER, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(3, 105, 161); // Premium blue hue
    doc.text("TAX INVOICE", PW / 2, y + 35.5, { align: "center" });
    doc.setTextColor(0, 0, 0); // reset
    line(MARGIN, y + 38, MARGIN + INNER, y + 38);

    // 3. METADATA ROW
    const metaY = y + 38;
    const metaH = 12;
    const colW = INNER / 3;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Invoice Number", MARGIN + 4, metaY + 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`#${order._id.slice(-8).toUpperCase()}`, MARGIN + 4, metaY + 9);

    line(MARGIN + colW, metaY, MARGIN + colW, metaY + metaH);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Date of Issue", MARGIN + colW + 4, metaY + 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(
      new Date(order.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
      MARGIN + colW + 4, metaY + 9
    );

    line(MARGIN + colW * 2, metaY, MARGIN + colW * 2, metaY + metaH);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Order Status", MARGIN + colW * 2 + 4, metaY + 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(order.status || "Pending", MARGIN + colW * 2 + 4, metaY + 9);

    line(MARGIN, metaY + metaH, MARGIN + INNER, metaY + metaH);

    // 4. SOLD BY (SELLER) vs SHIPPED TO (BUYER)
    const partyY = metaY + metaH;
    const partyH = 46;

    // Left block: Seller
    doc.setFillColor(245, 245, 245);
    rect(MARGIN, partyY, INNER / 2, 6, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("SOLD BY (SELLER)", MARGIN + 4, partyY + 4.2);

    // Right block: Buyer
    doc.setFillColor(245, 245, 245);
    rect(MID, partyY, INNER / 2, 6, "F");
    doc.text("SHIPPED TO (BUYER)", MID + 4, partyY + 4.2);

    line(MARGIN, partyY + 6, MARGIN + INNER, partyY + 6);
    line(MID, partyY, MID, partyY + partyH); // Vertical split

    // Render Seller (Vendor) Details
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const sellerName = vendor.businessName || vendor.name || "Vendor";
    doc.text(sellerName, MARGIN + 4, partyY + 11.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    let sY = partyY + 16.5;
    const sAddr = vendor.address || {};
    if (sAddr.street) {
      doc.text(sAddr.street, MARGIN + 4, sY, { maxWidth: INNER / 2 - 8 });
      sY += 4.5;
    }
    const sCityLine = [sAddr.district, sAddr.city].filter(Boolean).join(", ");
    if (sCityLine) {
      doc.text(sCityLine, MARGIN + 4, sY, { maxWidth: INNER / 2 - 8 });
      sY += 4.5;
    }
    const sStatePin = [sAddr.state, sAddr.pincode].filter(Boolean).join(" - ");
    if (sStatePin) {
      doc.text(sStatePin, MARGIN + 4, sY);
      sY += 4.5;
    }
    if (vendor.contactNumber) {
      doc.text(`Contact: ${vendor.contactNumber}`, MARGIN + 4, sY);
      sY += 4.5;
    }
    if (vendor.email) {
      doc.text(`Email: ${vendor.email}`, MARGIN + 4, sY, { maxWidth: INNER / 2 - 8 });
    }

    // Render Buyer (Customer) Details
    const buyer = order.userDetails || {};
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(buyer.name || "Customer", MID + 4, partyY + 11.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    let bY = partyY + 16.5;
    const bAddr = buyer.address || {};
    if (bAddr.address1) {
      doc.text(bAddr.address1, MID + 4, bY, { maxWidth: INNER / 2 - 8 });
      bY += 4.5;
    }
    if (bAddr.address2) {
      doc.text(bAddr.address2, MID + 4, bY, { maxWidth: INNER / 2 - 8 });
      bY += 4.5;
    }
    const bCityPin = [bAddr.city, bAddr.pincode].filter(Boolean).join(" - ");
    if (bCityPin) {
      doc.text(bCityPin, MID + 4, bY);
      bY += 4.5;
    }
    if (buyer.mobile) {
      doc.text(`Contact: ${buyer.mobile}`, MID + 4, bY);
      bY += 4.5;
    }
    if (buyer.email) {
      doc.text(`Email: ${buyer.email}`, MID + 4, bY, { maxWidth: INNER / 2 - 8 });
    }

    line(MARGIN, partyY + partyH, MARGIN + INNER, partyY + partyH);

    // 5. ITEMS TABLE
    const tableStartY = partyY + partyH;
    const tableData = (order.items || []).map((item, idx) => {
      const qty = item.quantity;
      const rate = Number(item.price) || 0;
      const amount = qty * rate;
      return [
        idx + 1,
        item.item?.name || "Deleted Item",
        item.item?.hsn || "—",
        qty,
        `Rs. ${rate.toFixed(2)}`,
        `Rs. ${amount.toFixed(2)}`
      ];
    });

    autoTable(doc, {
      startY: tableStartY,
      margin: { left: MARGIN, right: MARGIN },
      head: [["#", "Description of Goods", "HSN/SAC", "Qty", "Rate", "Amount"]],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 8.2,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        lineColor: [220, 220, 220],
        lineWidth: 0.15,
        textColor: [30, 30, 30]
      },
      headStyles: {
        fillColor: [30, 41, 59], // Elegant slate color
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        fontSize: 8.5
      },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 24, halign: "center" },
        3: { cellWidth: 16, halign: "center" },
        4: { cellWidth: 30, halign: "right" },
        5: { cellWidth: 30, halign: "right" }
      }
    });

    // 6. TOTALS & BANK DETAILS
    const tableEndY = doc.lastAutoTable.finalY;
    const subtotal = (order.items || []).reduce(
      (sum, item) => sum + (item.quantity * (Number(item.price) || 0)), 0
    );
    const tax = (Number(order.totalAmount) || 0) - subtotal;

    const totalsData = [
      ["Subtotal:", `Rs. ${subtotal.toFixed(2)}`],
      ...(tax > 0 ? [["Tax / GST:", `Rs. ${tax.toFixed(2)}`]] : []),
      ["Grand Total:", `Rs. ${Number(order.totalAmount || 0).toFixed(2)}`]
    ];

    // Draw Totals on Right Column
    autoTable(doc, {
      startY: tableEndY,
      margin: { left: MID, right: MARGIN },
      body: totalsData,
      theme: "plain",
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
        lineColor: [230, 230, 230],
        lineWidth: 0.15
      },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: "normal", textColor: [80, 80, 80] },
        1: { cellWidth: 45, halign: "right", fontStyle: "normal" }
      },
      didParseCell: (data) => {
        if (data.row.index === totalsData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 9.5;
          data.cell.styles.fillColor = [240, 246, 252];
          data.cell.styles.textColor = [3, 105, 161];
        }
      }
    });

    const totalsEndY = doc.lastAutoTable.finalY;

    // Draw Bank Details on Left Column (parallel to totals)
    const hasBank = vendor.bankDetails && vendor.bankDetails.bankName;
    if (hasBank) {
      const bank = vendor.bankDetails;
      let bY = tableEndY + 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text("BANK DETAILS FOR PAYMENT", MARGIN + 4, bY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      bY += 4.5;
      doc.text(`Bank Name: ${bank.bankName}`, MARGIN + 4, bY);
      bY += 4;
      doc.text(`A/c Holder: ${bank.accountHolderName}`, MARGIN + 4, bY);
      bY += 4;
      doc.text(`A/c Number: ${bank.accountNumber}`, MARGIN + 4, bY);
      bY += 4;
      doc.text(`IFSC Code: ${bank.ifscCode}`, MARGIN + 4, bY);
      if (bank.branchName) {
        bY += 4;
        doc.text(`Branch: ${bank.branchName}`, MARGIN + 4, bY);
      }
    }

    // Determine starting y for footer section
    const footerStartY = Math.max(totalsEndY, hasBank ? tableEndY + 28 : tableEndY) + 6;
    line(MARGIN, footerStartY, MARGIN + INNER, footerStartY);

    // Left: Declaration
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Declaration:", MARGIN + 4, footerStartY + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(
      "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.",
      MARGIN + 4, footerStartY + 10, { maxWidth: INNER / 2 - 8 }
    );

    // Right: Authorized Signatory
    line(MID, footerStartY, MID, footerStartY + 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`For ${sellerName}`, MID + 4, footerStartY + 5);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.text("(Authorized Signatory)", MID + 4, footerStartY + 28);

    line(MARGIN, footerStartY + 32, MARGIN + INNER, footerStartY + 32);

    // Bottom Computer Generated Invoice disclaimer
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(120, 120, 120);
    doc.text("This is a Computer Generated Invoice and does not require a physical signature.", PW / 2, footerStartY + 37, { align: "center" });

    doc.save(`Invoice_${order._id}.pdf`);
  };

  const statuses = ["All", "Pending", "Confirmed", "Shipped", "Delivered"];
  const filtered = filter === "All" ? orders : orders.filter(o => o.status === filter);

  if (loading) return <VendorLayout><div className="vp-loading">Loading orders...</div></VendorLayout>;

  return (
    <VendorLayout>
      <div className="vp-card">
        <div className="vp-card-header">
          <div className="vp-card-title">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
            All Orders ({orders.length})
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {statuses.map(s => (
              <button
                key={s}
                className={`vp-btn vp-btn-sm ${filter === s ? "vp-btn-primary" : "vp-btn-ghost"}`}
                onClick={() => setFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="vp-card-body" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div className="vp-empty"><p>No orders found for "{filter}".</p></div>
          ) : (
            <div className="vp-table-wrapper">
              <table className="vp-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Invoice</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(order => (
                    <tr key={order._id}>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>...{order._id.slice(-8)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{order.userDetails?.name || order.deliveryBoy?.name || "—"}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{order.userDetails?.mobile}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{order.userDetails?.email}</div>
                      </td>
                      <td>
                        {order.items.map((i, idx) => (
                          <div key={idx} style={{ fontSize: 12, marginBottom: 6 }}>
                            {/* Item name, qty, price */}
                            <div style={{ fontWeight: 600 }}>
                              {i.item?.name || "Deleted Item"} &times; {i.quantity} &mdash; &#8377;{i.price}
                            </div>
                            {/* Vendor-only cost price */}
                            {i.item?.originalPrice && (
                              <div style={{ marginTop: 3 }}>
                                <span style={{ color: "#b45309", fontWeight: 600, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "1px 8px", fontSize: "0.82em" }}>
                                  🔒 Cost: &#8377;{i.item.originalPrice}
                                </span>
                              </div>
                            )}
                            {/* Category badges — only shown if backend populates them */}
                            <div style={{ display: "flex", gap: 4, marginTop: 2, flexWrap: "wrap" }}>
                            </div>
                          </div>
                        ))}
                      </td>
                      {/* Category column */}
                      <td>
                        {order.items.map((i, idx) => (
                          i.item?.parentCategory?.name ? (
                            <span key={idx} style={{
                              display: "inline-block",
                              background: "#e0f2fe",
                              color: "#0369a1",
                              borderRadius: 4,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                              marginBottom: 3,
                            }}>
                              📁 {i.item.parentCategory.name}
                            </span>
                          ) : <span key={idx} style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>
                        ))}
                      </td>
                      {/* Sub Category column */}
                      <td>
                        {order.items.map((i, idx) => (
                          i.item?.subCategory?.name ? (
                            <span key={idx} style={{
                              display: "inline-block",
                              background: "#f0fdf4",
                              color: "#15803d",
                              borderRadius: 4,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                              marginBottom: 3,
                            }}>
                              🏷️ {i.item.subCategory.name}
                            </span>
                          ) : <span key={idx} style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>
                        ))}
                      </td>
                      <td style={{ fontWeight: 700 }}>&#8377;{order.totalAmount}</td>
                      <td>
                        <select
                          className="vp-status-select"
                          value={order.status}
                          onChange={e => handleStatusChange(order._id, e.target.value)}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="Shipped">Shipped</option>
                          <option value="Delivered">Delivered</option>
                        </select>
                      </td>
                      <td>
                        <button className="vp-btn vp-btn-ghost vp-btn-sm" onClick={() => generateInvoice(order, profile)}>
                          ↓ PDF
                        </button>
                      </td>
                      <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorAllOrders;
